// 認証セッション（PF34 Bearer）— signals ＋ IO（fetch/localStorage）。
// 純ロジックは session-core.mjs、ここは状態と副作用のみ。
import { signal, computed } from "@preact/signals";
import { config } from "../config";
import { SESSION_KEY, APP_ID, sessionFromVerify, needsLogin } from "./session-core.mjs";

export type SessionUser = { id: string; email: string; plan?: string } | null;
type Session = { token: string; user: SessionUser };

/** 現在のセッション（未ログインは null）。token は Bearer に使う。 */
export const sessionSig = signal<Session | null>(loadStored());
/** ログイン処理中フラグ（UI のスピナー用）。 */
export const authBusySig = signal<boolean>(false);
/** 直近の認証エラー文言（UI 表示用）。 */
export const authErrorSig = signal<string | null>(null);
/** マジックリンク送信済み（トークン貼付ステップへ）フラグ。 */
export const magicSentSig = signal<boolean>(false);

/** ログイン必須か（本番で未ログインなら true。ローカルは devUser があるので false）。 */
export const requiresLoginSig = computed<boolean>(() =>
  needsLogin({ token: sessionSig.value?.token, devUser: config.devUser }),
);

/** データ API に渡す現在の Bearer トークン（api/client が参照）。 */
export function currentToken(): string | undefined {
  return sessionSig.value?.token;
}

function loadStored(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    return s?.token ? s : null;
  } catch {
    return null;
  }
}

function store(s: Session | null): void {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* localStorage 不可（プライベートモード等）でもメモリ上の signal で継続 */
  }
}

async function post(path: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(`${config.apiBase}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || data.ok === false) throw new Error((data.error as string) ?? `HTTP ${res.status}`);
  return data;
}

/** ① ログインリンクをメール送信（POST /auth/magic-link）。 */
export async function requestMagicLink(email: string): Promise<void> {
  authBusySig.value = true;
  authErrorSig.value = null;
  try {
    await post("/auth/magic-link", { email: email.trim(), app: APP_ID });
    magicSentSig.value = true;
  } catch (e) {
    authErrorSig.value = (e as Error).message;
  } finally {
    authBusySig.value = false;
  }
}

/** ② メール内リンクのトークンを貼付して交換（POST /auth/verify）。成功でログイン確定。 */
export async function verifyToken(token: string): Promise<boolean> {
  authBusySig.value = true;
  authErrorSig.value = null;
  try {
    const data = await post("/auth/verify", { token: token.trim() });
    const s = sessionFromVerify(data) as Session;
    sessionSig.value = s;
    store(s);
    magicSentSig.value = false;
    return true;
  } catch (e) {
    authErrorSig.value = (e as Error).message;
    return false;
  } finally {
    authBusySig.value = false;
  }
}

/** 起動時: 保存トークンがあれば /me で検証（失効なら破棄）。 */
export async function restoreSession(): Promise<void> {
  const token = sessionSig.value?.token;
  if (!token) return;
  try {
    const res = await fetch(`${config.apiBase}/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { clearSession(); return; }
    const data = (await res.json().catch(() => ({}))) as { user?: SessionUser };
    if (data.user) sessionSig.value = { token, user: data.user };
  } catch {
    /* オフライン等はトークンを保持（次回のデータ呼び出しで再判定） */
  }
}

/** 401 を受けたときにセッションを破棄する（api/client から呼ぶ）。 */
export function clearSession(): void {
  sessionSig.value = null;
  store(null);
  magicSentSig.value = false;
}

/** 明示ログアウト。 */
export function logout(): void {
  clearSession();
}
